import type { StudioGenerationAuthorityReadback } from '@/features/generation-authority/generation-authority-readback'
import {
  getAuthorityContinuityCopy,
  type AuthorityContinuityCopy,
} from '../authority-continuity-copy'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'

export interface AuthorityContinuityViewModel {
  eyebrow: string
  title: string
  summary: string
  facts: Array<{ label: string; value: string }>
  primaryAction: {
    label: string
    href?: string
    command?: 'continue-chat' | 'review-contract' | 'open-conformance'
  }
  secondaryAction?: { label: string; href: string }
  tone: 'neutral' | 'needs-input' | 'ready' | 'blocked'
}

interface BuildAuthorityContinuityViewModelInput {
  readback: StudioGenerationAuthorityReadback
  projectName?: string
  blockingCount: number
  /** Defaults to English for non-React consumers; Overview always supplies it. */
  locale?: LocaleCode
  /**
   * Whether this workspace has any project at all. Defaults to `true` so
   * callers that cannot know stay on the existing behavior; Overview knows and
   * passes it. See `buildAuthorityContinuityViewModel` for what it suppresses.
   */
  hasExistingWork?: boolean
}

function compactReferenceFingerprint(reference: string): string {
  let high = 0x811c9dc5
  let low = 0x9e3779b9
  for (let index = 0; index < reference.length; index += 1) {
    const code = reference.charCodeAt(index)
    high = Math.imul(high ^ code, 0x01000193) >>> 0
    low = Math.imul(low ^ code, 0x85ebca6b) >>> 0
  }
  return `${high.toString(16).padStart(8, '0')}${low
    .toString(16)
    .padStart(8, '0')}`
}

function humanizeSafeIdentifier(identifier: string): string | null {
  if (
    identifier.length === 0 ||
    identifier.length > 80 ||
    !/^[a-z0-9][a-z0-9._:-]*$/i.test(identifier)
  ) {
    return null
  }
  const compact = identifier.replace(/[._:-]+/g, ' ').trim()
  if (compact.length === 0) return null
  const fingerprintCandidate = identifier.replace(/[._:-]+/g, '')
  if (/^[a-f0-9]{16,}$/i.test(fingerprintCandidate)) return null
  return compact
}

const RESERVED_IDENTITY_PHRASES = [
  'creator empty',
  'design system only',
  'app spec only',
  'partial mixed',
  'contract exact',
  'gap',
  'authority envelope',
] as const

const RESERVED_IDENTITY_SUBSTRINGS = ['qwen', 'gemma', 'frontier'] as const

function containsReservedIdentity(identifier: string): boolean {
  const normalized = identifier
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
  if (
    RESERVED_IDENTITY_SUBSTRINGS.some((token) => normalized.includes(token))
  ) {
    return true
  }
  const padded = ` ${normalized} `
  return RESERVED_IDENTITY_PHRASES.some((phrase) =>
    padded.includes(` ${phrase} `),
  )
}

function describeRegisteredSource(
  authorityRef: string,
  copy: AuthorityContinuityCopy,
): string {
  const sourceHash = /^source-design:sha256:[a-f0-9]{32,}$/i.test(
    authorityRef,
  )
  if (sourceHash) {
    return copy.sourceFingerprint(compactReferenceFingerprint(authorityRef))
  }
  if (authorityRef === 'source-design:studio-generation-context') {
    return copy.currentStudioSource(compactReferenceFingerprint(authorityRef))
  }

  const knownForms = [
    ['design-pack:', 'designPack'],
    ['design-preset:', 'designPreset'],
    ['design-ontology:', 'designLibrary'],
    ['studio-outcome:', 'selectedOutcome'],
  ] as const satisfies ReadonlyArray<readonly [
    string,
    Parameters<AuthorityContinuityCopy['registeredSourceWithIdentity']>[0],
  ]>
  for (const [prefix, label] of knownForms) {
    if (!authorityRef.startsWith(prefix)) continue
    const identifier = authorityRef.slice(prefix.length)
    const humanized = humanizeSafeIdentifier(identifier)
    const fingerprint = compactReferenceFingerprint(authorityRef)
    if (humanized && !containsReservedIdentity(identifier)) {
      return copy.registeredSourceWithIdentity(label, humanized, fingerprint)
    }
    return copy.registeredSourceFingerprint(label, fingerprint)
  }

  return copy.registeredSource
}

function buildInvalidReadbackModel(copy: AuthorityContinuityCopy): AuthorityContinuityViewModel {
  return {
    eyebrow: copy.generationPaused,
    title: copy.evidenceNeedsReview,
    summary: copy.invalidReadbackSummary,
    facts: [
      {
        label: copy.nextStep,
        value: copy.reviewSavedEvidence,
      },
    ],
    primaryAction: {
      label: copy.reviewEvidence,
      command: 'review-contract',
    },
    tone: 'blocked',
  }
}

function buildAbsentReadbackModel(
  projectName: string | undefined,
  copy: AuthorityContinuityCopy,
): AuthorityContinuityViewModel {
  return {
    eyebrow: copy.continueBuilding,
    title: copy.continueApplication(projectName),
    summary: copy.absentReadbackSummary,
    facts: [],
    primaryAction: {
      label: copy.continueDescribingApplication,
      command: 'continue-chat',
    },
    tone: 'neutral',
  }
}

function buildFirstRunModel(
  copy: AuthorityContinuityCopy,
): AuthorityContinuityViewModel {
  return {
    eyebrow: copy.startBuilding,
    title: copy.describeFirstApplication,
    summary: copy.firstRunSummary,
    facts: [],
    primaryAction: {
      label: copy.describeApplicationAction,
      command: 'continue-chat',
    },
    tone: 'neutral',
  }
}

function applyBlockingState(
  model: AuthorityContinuityViewModel,
  blockingCount: number,
  copy: AuthorityContinuityCopy,
): AuthorityContinuityViewModel {
  if (blockingCount <= 0) return model

  return {
    ...model,
    eyebrow: copy.requiredChecks,
    title: copy.resolveRequiredChecks,
    summary: copy.blockingSummary,
    facts: [
      ...model.facts,
      {
        label: copy.requiredChecks,
        value: copy.requiredChecksToResolve(blockingCount),
      },
    ],
    primaryAction: {
      label: copy.reviewRequiredChecks,
      command: 'review-contract',
    },
    secondaryAction: undefined,
    tone: 'blocked',
  }
}

function buildAvailableReadbackModel(
  readback: Extract<StudioGenerationAuthorityReadback, { status: 'available' }>,
  projectName: string | undefined,
  copy: AuthorityContinuityCopy,
): AuthorityContinuityViewModel {
  const { envelope } = readback

  switch (envelope.profileId) {
    case 'creator-empty':
      return {
        eyebrow: copy.nextStepEyebrow,
        title: copy.describeApplication(projectName),
        summary: copy.creatorEmptySummary,
        facts: [],
        primaryAction: {
          label: copy.continueDescribingApplication,
          command: 'continue-chat',
        },
        tone: 'neutral',
      }

    case 'design-system-only':
      return {
        eyebrow: copy.visualDirectionSelected,
        title: copy.describeHowApplicationWorks,
        summary: copy.designOnlySummary,
        facts: [
          {
            label: copy.visualSystem,
            value: copy.selectedVisualSystem,
          },
        ],
        primaryAction: {
          label: copy.describeApplicationBehavior,
          command: 'continue-chat',
        },
        tone: 'needs-input',
      }

    case 'app-spec-only':
      return {
        eyebrow: copy.applicationBehaviorPreserved,
        title: copy.chooseVisualDirection,
        summary: copy.appSpecOnlySummary,
        facts: [
          {
            label: copy.applicationFlows,
            value: copy.suppliedApplicationFlows,
          },
        ],
        primaryAction: {
          label: copy.describeVisualDirection,
          command: 'continue-chat',
        },
        tone: 'needs-input',
      }

    case 'partial-mixed': {
      const acceptedCategories = envelope.designAuthority.lockedAxes.map(
        (axis) => copy.designAxes[axis],
      )
      if (envelope.applicationAuthority.lockedPaths.length > 0) {
        acceptedCategories.push(
          copy.suppliedApplicationFlowsCategory ?? copy.suppliedApplicationFlows,
        )
      }
      const remainingCategories = envelope.designAuthority.unresolvedAxes.map(
        (axis) => copy.designAxes[axis],
      )
      if (envelope.applicationAuthority.openSlots.length > 0) {
        remainingCategories.push(copy.applicationDetails)
      }
      if (envelope.questions.length > 0) {
        remainingCategories.push(copy.clarificationAnswers)
      }

      const facts: AuthorityContinuityViewModel['facts'] = []
      if (acceptedCategories.length > 0) {
        facts.push({
          label: copy.accepted,
          value: copy.joinCategories(acceptedCategories),
        })
      }
      if (remainingCategories.length > 0) {
        facts.push({
          label: copy.stillToDecide,
          value: copy.joinCategories(remainingCategories),
        })
      }

      if (acceptedCategories.length === 0 && remainingCategories.length === 0) {
        return {
          eyebrow: copy.reviewProjectDetails,
          title: copy.confirmWhatShouldBeKept,
          summary: copy.noDecisionCategoriesSummary,
          facts,
          primaryAction: {
            label: copy.reviewProjectRequirements,
            command: 'review-contract',
          },
          tone: 'neutral',
        }
      }

      if (remainingCategories.length === 0) {
        return {
          eyebrow: copy.requirementsRecorded,
          title: copy.reviewAcceptedRequirements,
          summary: copy.acceptedFactsSummary,
          facts,
          primaryAction: {
            label: copy.reviewAcceptedRequirementsAction,
            command: 'review-contract',
          },
          tone: 'neutral',
        }
      }

      if (acceptedCategories.length === 0) {
        return {
          eyebrow: copy.decisionsStillNeeded,
          title: copy.finishOpenDecisions,
          summary: copy.openDecisionsSummary,
          facts,
          primaryAction: {
            label: copy.continueOpenDecisions,
            command: 'continue-chat',
          },
          tone: 'needs-input',
        }
      }

      return {
        eyebrow: copy.someDecisionsSet,
        title: copy.finishRemainingDecisions,
        summary: copy.mixedDecisionsSummary,
        facts,
        primaryAction: {
          label: copy.continueRemainingDecisions,
          command: 'continue-chat',
        },
        tone: 'needs-input',
      }
    }

    case 'contract-exact':
      return {
        eyebrow: copy.exactRequirementsLocked,
        title: copy.reviewConformanceRequirements(projectName),
        summary: copy.exactRequirementsSummary,
        facts: [
          {
            label: copy.visualSource,
            value: describeRegisteredSource(
              envelope.designAuthority.authorityRef,
              copy,
            ),
          },
          {
            label: copy.applicationContract,
            value: copy.suppliedApplicationContract,
          },
          {
            label: copy.lockedRequirements,
            value: copy.lockedRequirementsValue,
          },
        ],
        primaryAction: {
          label: copy.reviewConformanceRequirementsAction,
          command: 'open-conformance',
        },
        tone: 'neutral',
      }
  }
}

/**
 * On a workspace with no project and no source read back, this region used to
 * say "Continue building / Continue your application" over a body admitting it
 * had inferred nothing, directly above an empty state reading "no work yet" —
 * naming an application that does not exist (measured on the appliance
 * 2026-08-06 on a fresh account).
 *
 * The region still belongs there: its action focuses the Home composer, so it
 * is the on-ramp, not decoration. Only the false claim is removed — a
 * first-run workspace gets first-run wording, and a workspace that HAS a
 * project keeps "continue", where it is true and the missing source is the
 * thing to act on.
 */
export function buildAuthorityContinuityViewModel(
  input: BuildAuthorityContinuityViewModelInput,
): AuthorityContinuityViewModel {
  const copy = getAuthorityContinuityCopy(input.locale ?? 'en')
  if (input.readback.status === 'invalid') {
    return applyBlockingState(
      buildInvalidReadbackModel(copy),
      input.blockingCount,
      copy,
    )
  }
  if (input.readback.status === 'absent') {
    return applyBlockingState(
      input.hasExistingWork === false
        ? buildFirstRunModel(copy)
        : buildAbsentReadbackModel(input.projectName, copy),
      input.blockingCount,
      copy,
    )
  }

  return applyBlockingState(
    buildAvailableReadbackModel(input.readback, input.projectName, copy),
    input.blockingCount,
    copy,
  )
}
